/**
 * Translation lookup shared by the web and the Android app, so `{name}` placeholders, locale fallback
 * and "unknown key shows the key" behave identically on both. Each platform owns its English source
 * tree and its translation tables; this file owns only the mechanics.
 */

/** Dotted paths to every string leaf of a nested dictionary: `{ common: { save: "" } }` → `"common.save"`. */
export type Leaves<T, P extends string = ""> = T extends string
  ? P
  : { [K in keyof T & string]: Leaves<T[K], P extends "" ? K : `${P}.${K}`> }[keyof T & string];

export type TranslationVars = Record<string, string | number>;

/** `{n}` → value. Placeholders without a value are left as written, so a missing variable is visible. */
export function interpolate(template: string, vars?: TranslationVars): string {
  if (!vars) return template;
  let s = template;
  for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/** Nested dictionary → `{ "common.save": "Save" }`. */
export function flattenDictionary(tree: object, prefix = ""): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(tree)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") out[path] = v;
    else if (v && typeof v === "object") Object.assign(out, flattenDictionary(v, path));
  }
  return out;
}

/**
 * Picks the best available language for a BCP-47 tag. Apple's folder names are the shared vocabulary
 * (`pt-BR`, `pt-PT`, `zh-Hans`, `nb`): "no"/"nn" resolve to `nb`, Chinese without a script to `zh-Hans`,
 * bare Portuguese to `pt-BR`. Anything unknown falls back to `fallback`.
 */
export function resolveLocale(tag: string | null | undefined, available: readonly string[], fallback = "en"): string {
  if (!tag) return fallback;
  const has = (c: string) => available.find((a) => a.toLowerCase() === c.toLowerCase());
  const [lang = "", ...rest] = tag.replace(/_/g, "-").split("-");
  const l = lang.toLowerCase();
  const exact = has(tag.replace(/_/g, "-"));
  if (exact) return exact;
  if (l === "zh") {
    const traditional = rest.some((p) => /^(hant|tw|hk|mo)$/i.test(p));
    return (!traditional && has("zh-Hans")) || fallback;
  }
  if (l === "no" || l === "nn" || l === "nb") return has("nb") ?? fallback;
  if (l === "pt") {
    const portugal = rest.some((p) => /^pt$/i.test(p));
    return (portugal ? has("pt-PT") : has("pt-BR")) ?? has("pt-BR") ?? has("pt-PT") ?? fallback;
  }
  return has(l) ?? fallback;
}

export interface I18nOptions<D extends object> {
  /** The English source tree. Its shape defines every valid key. */
  source: D;
  /** Flat `"group.key" → text` tables per language, partial: a missing key shows the English text. */
  translations?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  /** The device language as a BCP-47 tag, read on every call so a language change needs no reload. */
  locale: () => string;
  fallback?: string;
}

export function createI18n<D extends object>({ source, translations = {}, locale, fallback = "en" }: I18nOptions<D>) {
  const english = flattenDictionary(source);
  const languages = [fallback, ...Object.keys(translations)];
  const table = () => translations[resolveLocale(locale(), languages, fallback)];

  /** `t("modules.guests")` → "Guests". `{n}` placeholders are replaced from `vars`. */
  function t(key: Leaves<D>, vars?: TranslationVars): string {
    const k = key as string;
    return interpolate(table()?.[k] ?? english[k] ?? k, vars);
  }

  /** Enum label lookup (`label("rsvp", "accepted")`); an unknown value from a newer client shows as itself. */
  function label(group: keyof D & string, value: string): string {
    const k = `${group}.${value}`;
    return table()?.[k] ?? english[k] ?? value;
  }

  return { t, label, language: () => resolveLocale(locale(), languages, fallback) };
}
